// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { isChunkLoadError, reloadForFreshAssets } from '../utils/chunk-reload.js';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // A failed lazy-chunk load after a deploy can't be fixed by re-rendering —
    // auto-reload once to fetch the freshly-deployed assets. The reload is
    // guarded against loops; if it doesn't fire (already reloaded), the fallback
    // below still offers a manual reload.
    if (isChunkLoadError(error)) {
      void reloadForFreshAssets();
      return;
    }
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, isChunkError: false });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Stale-chunk failure: a re-render won't recover it, so lead with a full
      // reload (a new version is available).
      if (this.state.isChunkError) {
        return (
          <div className="dm-error-boundary">
            <div className="dm-error-boundary-card">
              <h2>A new version is available</h2>
              <p className="dm-error-boundary-message">The app was updated. Reload to continue.</p>
              <div className="dm-error-boundary-actions">
                <button className="dm-btn dm-btn-primary" onClick={this.handleReload}>
                  Reload
                </button>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="dm-error-boundary">
          <div className="dm-error-boundary-card">
            <h2>Something went wrong</h2>
            {this.state.error && (
              <p className="dm-error-boundary-message">{this.state.error.message}</p>
            )}
            <div className="dm-error-boundary-actions">
              <button className="dm-btn dm-btn-primary" onClick={this.handleRetry}>
                Retry
              </button>
              <a href="/admin/" className="dm-btn dm-btn-secondary">
                Go to Dashboard
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
